import {
  DEFAULT_PROVIDER_INTERACTION_MODE,
  DEFAULT_RUNTIME_MODE,
  type EnvironmentApi,
  type EnvironmentId,
  type ModelSelection,
  type ProjectId,
  type ProviderInteractionMode,
  type RuntimeMode,
  type ServerProvider,
  type ThreadId,
  type ThreadRuntimeOption,
} from "@cmmd-forge/contracts";
import { scopeProjectRef } from "@cmmd-forge/client-runtime";

import { buildPrimaryFetchInit } from "~/environments/primary/forgeSessionClient";
import { resolvePrimaryEnvironmentHttpUrl } from "~/environments/primary";
import { newCommandId, newMessageId, newThreadId } from "../lib/utils";
import {
  getDefaultServerModel,
  resolveEligibleLaunchProvider,
  resolveEligibleSelectableProvider,
  resolveSelectableProvider,
} from "../providerModels";
import {
  fetchForgeProviderAccess,
  type ForgeProviderAccessState,
  type ProviderCredentialMode,
} from "../cmmd/forgeProviderAccess";
import { classifyEnvironmentId } from "../environments/environmentIdClassification";
import type { Project } from "../types";
import { resolveNewThreadRuntimeProjectRef } from "~/lib/threadRuntimeTarget";
import type { TaskThreadStartPayload } from "./NoActiveThreadState.threadLaunch";
import { resolveTaskThreadStartMetadata } from "./command-center/taskThreadContext";
import { readHttpErrorMessage, shouldAutoStartTaskThread } from "./NoActiveThreadState.logic";
import { ensureDesktopRuntimeEnvironmentApi } from "~/lib/ensureDesktopRuntimeEnvironmentApi";
import { useSavedEnvironmentRuntimeStore } from "~/environments/runtime/savedEnvironmentRuntimeStore";
import { waitForAuthorizedProviderLaunch } from "../lib/providerInventoryReadiness";
import { getServerConfig } from "~/rpc/serverState";
import {
  resolveExactProviderModelPair,
  type ExactProviderModelPairResolution,
} from "./newThread/NewThreadLanding.logic";
import {
  resolveWorktreeBootstrap,
  resolveWorktreeBootstrapFromConfiguration,
} from "./NoActiveThreadState.threadRunner.worktree";

export interface ThreadLaunchResult {
  readonly autoStarted: boolean;
  readonly providerHint: string | null;
  readonly threadId: ThreadId;
  readonly environmentId: EnvironmentId;
}

interface ProjectRef {
  readonly environmentId: EnvironmentId;
  readonly projectId: ProjectId;
}

export interface ThreadLaunchProject extends Pick<
  Project,
  "environmentId" | "id" | "repositoryIdentity" | "cwd"
> {
  readonly id: ProjectId;
  readonly environmentId: EnvironmentId;
  readonly defaultModelSelection?: ModelSelection | null;
}

export interface ThreadLaunchConfiguration {
  readonly modelSelection: ModelSelection | null;
  readonly runtimeMode: RuntimeMode;
  readonly interactionMode: ProviderInteractionMode;
  readonly environmentMode: "local" | "worktree";
  readonly baseBranch: string | null;
}

export interface StartThreadForItemInput {
  readonly itemId: string;
  readonly threadId?: ThreadId;
  readonly autoStart?: boolean;
  readonly launchConfiguration?: ThreadLaunchConfiguration;
  readonly importedProjectRef?: ProjectRef | undefined;
  readonly importedFromCmmdProject?: boolean;
  readonly payload: TaskThreadStartPayload;
  readonly workspaceProject: ThreadLaunchProject | null;
  readonly defaultProjectRef: ProjectRef | null;
  readonly projects: readonly ThreadLaunchProject[];
  readonly primaryEnvironmentId: EnvironmentId | null;
  readonly defaultThreadRuntime: ThreadRuntimeOption;
  readonly serverProviders: readonly ServerProvider[];
  readonly cmmdTaskTitleById: ReadonlyMap<string, string>;
  readonly resolveImportedCmmdProjectRef: (itemId: string) => ProjectRef | null;
  readonly readEnvironmentApi: (environmentId: EnvironmentId) => EnvironmentApi | undefined;
  readonly loadProviderAccess?: (
    credentialMode: ProviderCredentialMode,
    runtimeId: string | null,
  ) => Promise<Extract<ForgeProviderAccessState, { status: "ready" }>>;
  readonly readEnvironmentProviders?: (environmentId: EnvironmentId) => readonly ServerProvider[];
  readonly preparedProviderLaunch?: PreparedThreadProviderLaunch;
}

const PREPARED_THREAD_PROVIDER_LAUNCH = Symbol("prepared-thread-provider-launch");

export interface PreparedThreadProviderLaunch {
  readonly [PREPARED_THREAD_PROVIDER_LAUNCH]: true;
  readonly environmentId: EnvironmentId;
  readonly launchModel: NonNullable<ReturnType<typeof resolveThreadLaunchModel>>;
}

export interface PrepareThreadProviderLaunchInput {
  readonly environmentId: EnvironmentId;
  readonly primaryEnvironmentId: EnvironmentId | null;
  readonly primaryProviders: readonly ServerProvider[];
  readonly preferred: ModelSelection | null | undefined;
  readonly pinPreferredProvider: boolean;
  readonly loadProviderAccess?: StartThreadForItemInput["loadProviderAccess"];
  readonly readEnvironmentProviders?: StartThreadForItemInput["readEnvironmentProviders"];
}

export async function startThreadForItem(
  input: StartThreadForItemInput,
): Promise<ThreadLaunchResult> {
  const requestedProjectRef = resolveRequestedProjectRef(input);
  const targetProjectRef = resolveTargetProjectRef(input, requestedProjectRef);
  const targetProject = findTargetProject(input.projects, targetProjectRef);
  const api = targetProjectRef
    ? await ensureDesktopRuntimeEnvironmentApi({
        environmentId: targetProjectRef.environmentId,
        primaryEnvironmentId: input.primaryEnvironmentId,
        label: targetProject?.repositoryIdentity?.name ?? "Desktop runtime",
        readApi: input.readEnvironmentApi,
      })
    : undefined;
  const canAutoStart =
    input.autoStart !== false &&
    shouldAutoStartTaskThread({
      cmmdTaskId: input.payload.cmmdTaskId,
      targetProject,
      importedFromCmmdProject: input.importedFromCmmdProject === true,
    });
  const providerEnvironmentId = resolveProviderEnvironmentId(input, targetProjectRef);
  const preferredModelSelection =
    input.launchConfiguration?.modelSelection ?? targetProject?.defaultModelSelection ?? null;
  const launchModel = canAutoStart
    ? await resolveAutoStartLaunchModel({
        input,
        providerEnvironmentId,
        preferredModelSelection,
      })
    : resolveCreateOnlyLaunchModel({
        preferred: preferredModelSelection,
        pinPreferredSelection: (input.launchConfiguration?.modelSelection ?? null) !== null,
        serverProviders: readTargetEnvironmentProviders({
          environmentId: providerEnvironmentId,
          primaryEnvironmentId: input.primaryEnvironmentId,
          primaryProviders: input.serverProviders,
          readEnvironmentProviders: input.readEnvironmentProviders,
        }),
      });
  const threadId = input.threadId ?? newThreadId();
  const threadCreatedAt = new Date().toISOString();
  const startMetadata = resolveTaskThreadStartMetadata({
    itemId: input.itemId,
    payloadTitle: input.payload.title,
    payloadCmmdTaskId: input.payload.cmmdTaskId,
    cmmdTaskTitleById: input.cmmdTaskTitleById,
  });

  if (!api) {
    return startWebThread({
      input,
      targetProjectRef,
      targetProject,
      launchModel,
      canAutoStart,
      threadId,
      threadCreatedAt,
      startMetadata,
    });
  }

  if (!targetProjectRef) {
    throw new Error("No project is available for starting a thread.");
  }

  return startLocalThread({
    api,
    targetProjectRef,
    targetProject,
    launchConfiguration: input.launchConfiguration,
    payload: input.payload,
    launchModel,
    canAutoStart,
    threadId,
    threadCreatedAt,
    startMetadata,
  });
}

async function resolveAutoStartLaunchModel(input: {
  readonly input: StartThreadForItemInput;
  readonly providerEnvironmentId: EnvironmentId;
  readonly preferredModelSelection: ModelSelection | null;
}): Promise<ResolvedThreadLaunchModel> {
  const preparedProviderLaunch =
    input.input.preparedProviderLaunch ??
    (await prepareThreadProviderLaunch({
      environmentId: input.providerEnvironmentId,
      primaryEnvironmentId: input.input.primaryEnvironmentId,
      primaryProviders: input.input.serverProviders,
      preferred: input.preferredModelSelection,
      pinPreferredProvider: (input.input.launchConfiguration?.modelSelection ?? null) !== null,
      loadProviderAccess: input.input.loadProviderAccess,
      readEnvironmentProviders: input.input.readEnvironmentProviders,
    }));
  if (preparedProviderLaunch.environmentId !== input.providerEnvironmentId) {
    throw new Error("The selected provider access belongs to a different Forge runtime.");
  }
  return preparedProviderLaunch.launchModel;
}

function resolveCreateOnlyLaunchModel(input: {
  readonly preferred: ModelSelection | null;
  readonly pinPreferredSelection: boolean;
  readonly serverProviders: readonly ServerProvider[];
}): ResolvedThreadLaunchModel {
  const preferredPair = input.preferred
    ? resolveExactProviderModelPair({
        providers: input.serverProviders,
        provider: input.preferred.provider,
        model: input.preferred.model,
      })
    : null;
  if (
    preferredPair?.status === "foreign" ||
    (input.pinPreferredSelection && preferredPair?.status !== "available")
  ) {
    throw new Error(
      "The selected provider and model are not available on this Environment. Choose an available model and try again.",
    );
  }
  const provider = resolveSelectableProvider(
    input.serverProviders,
    preferredProvider(input.preferred),
  );
  return buildResolvedThreadLaunchModel(input, provider, preferredPair);
}

function resolveRequestedProjectRef(input: StartThreadForItemInput): ProjectRef | null {
  return (
    input.importedProjectRef ??
    workspaceProjectRef(input.workspaceProject) ??
    input.resolveImportedCmmdProjectRef(input.itemId) ??
    input.defaultProjectRef ??
    firstProjectRef(input.projects)
  );
}

function workspaceProjectRef(project: ThreadLaunchProject | null): ProjectRef | null {
  return project ? { environmentId: project.environmentId, projectId: project.id } : null;
}

function firstProjectRef(projects: readonly ThreadLaunchProject[]): ProjectRef | null {
  const first = projects[0];
  return first ? { environmentId: first.environmentId, projectId: first.id } : null;
}

function resolveTargetProjectRef(
  input: StartThreadForItemInput,
  requestedProjectRef: ProjectRef | null,
): ProjectRef | null {
  if (!requestedProjectRef) return null;
  return resolveNewThreadRuntimeProjectRef({
    requestedRuntime: input.defaultThreadRuntime,
    primaryEnvironmentId: input.primaryEnvironmentId,
    projects: input.projects,
    projectRef: scopeProjectRef(requestedProjectRef.environmentId, requestedProjectRef.projectId),
  });
}

function findTargetProject(
  projects: readonly ThreadLaunchProject[],
  targetProjectRef: ProjectRef | null,
): ThreadLaunchProject | null {
  if (!targetProjectRef) return null;
  return (
    projects.find(
      (project) =>
        project.environmentId === targetProjectRef.environmentId &&
        project.id === targetProjectRef.projectId,
    ) ?? null
  );
}

function resolveThreadLaunchModel(input: {
  readonly preferred: ModelSelection | null | undefined;
  readonly pinPreferredProvider: boolean;
  readonly serverProviders: readonly ServerProvider[];
  readonly providerAccess: ForgeProviderAccessState;
  readonly credentialMode: ProviderCredentialMode;
}): {
  readonly modelSelection: ModelSelection;
  readonly providerHint: string | null;
} | null {
  const preferredPair = input.preferred
    ? resolveExactProviderModelPair({
        providers: input.serverProviders,
        provider: input.preferred.provider,
        model: input.preferred.model,
      })
    : null;
  if (preferredPair?.status === "foreign") return null;
  if (input.pinPreferredProvider && preferredPair?.status !== "available") return null;

  const preferred = preferredProvider(input.preferred);
  const resolvedProvider = input.pinPreferredProvider
    ? resolveEligibleLaunchProvider(
        input.serverProviders,
        preferred,
        input.providerAccess,
        input.credentialMode,
      )
    : resolveEligibleSelectableProvider(
        input.serverProviders,
        preferred,
        input.providerAccess,
        input.credentialMode,
      );
  if (resolvedProvider === null) return null;
  return buildResolvedThreadLaunchModel(input, resolvedProvider, preferredPair);
}

function preferredProvider(preferred: ModelSelection | null | undefined) {
  return preferred?.provider ?? null;
}

function buildResolvedThreadLaunchModel(
  input: Pick<Parameters<typeof resolveThreadLaunchModel>[0], "preferred" | "serverProviders">,
  resolvedProvider: ModelSelection["provider"],
  preferredPair: ExactProviderModelPairResolution | null,
): NonNullable<ReturnType<typeof resolveThreadLaunchModel>> {
  const resolvedSnapshot = input.serverProviders.find(
    (provider) => provider.provider === resolvedProvider,
  );
  const preferredModel = resolvePreferredPairModel(
    input.preferred,
    resolvedProvider,
    preferredPair,
  );

  return {
    modelSelection: {
      provider: resolvedProvider,
      model: preferredModel ?? getDefaultServerModel(input.serverProviders, resolvedProvider),
      ...(preferredModel && input.preferred?.options ? { options: input.preferred.options } : {}),
    } as ModelSelection,
    providerHint: resolvedSnapshot?.message ?? null,
  };
}

function resolvePreferredPairModel(
  preferred: ModelSelection | null | undefined,
  resolvedProvider: ModelSelection["provider"],
  preferredPair: ExactProviderModelPairResolution | null,
): string | null {
  if (preferred?.provider !== resolvedProvider) return null;
  return preferredPair?.status === "available" ? preferredPair.model : null;
}

export async function prepareThreadProviderLaunch(
  input: PrepareThreadProviderLaunchInput,
): Promise<PreparedThreadProviderLaunch> {
  const classification = classifyEnvironmentId(input.environmentId);
  const credentialMode: ProviderCredentialMode = classification.isCloud ? "cmmd-cloud" : "runtime";
  const runtimeId = classification.legacyRuntimeId;
  const loadProviderAccess = input.loadProviderAccess ?? fetchForgeProviderAccess;
  const launchModel = await waitForAuthorizedProviderLaunch({
    loadProviderAccess: () => loadProviderAccess(credentialMode, runtimeId),
    readProviders: () => readTargetEnvironmentProviders(input),
    credentialMode,
    pinnedProvider: input.pinPreferredProvider ? preferredProvider(input.preferred) : null,
    resolve: ({ providerAccess, providers }) =>
      resolveThreadLaunchModel({
        preferred: input.preferred,
        pinPreferredProvider: input.pinPreferredProvider,
        serverProviders: providers,
        providerAccess,
        credentialMode,
      }),
  });
  return {
    [PREPARED_THREAD_PROVIDER_LAUNCH]: true,
    environmentId: input.environmentId,
    launchModel,
  };
}

function resolveProviderEnvironmentId(
  input: StartThreadForItemInput,
  targetProjectRef: ProjectRef | null,
): EnvironmentId {
  return targetProjectRef?.environmentId ?? resolveEmptyProjectEnvironmentId(input);
}

function readTargetEnvironmentProviders(
  input: Pick<
    PrepareThreadProviderLaunchInput,
    "environmentId" | "primaryEnvironmentId" | "primaryProviders" | "readEnvironmentProviders"
  >,
): readonly ServerProvider[] {
  if (input.readEnvironmentProviders) {
    return input.readEnvironmentProviders(input.environmentId);
  }
  if (input.environmentId === input.primaryEnvironmentId) {
    return getServerConfig()?.providers ?? input.primaryProviders;
  }
  return (
    useSavedEnvironmentRuntimeStore.getState().byId[input.environmentId]?.serverConfig?.providers ??
    []
  );
}

async function startWebThread(input: {
  readonly input: StartThreadForItemInput;
  readonly targetProjectRef: ProjectRef | null;
  readonly targetProject: ThreadLaunchProject | null;
  readonly launchModel: ResolvedThreadLaunchModel;
  readonly canAutoStart: boolean;
  readonly threadId: ThreadId;
  readonly threadCreatedAt: string;
  readonly startMetadata: ResolvedTaskThreadStartMetadata;
}): Promise<ThreadLaunchResult> {
  const webProjectId = await resolveWebProjectId(input.targetProjectRef);
  const webEnvironmentId =
    input.targetProjectRef?.environmentId ?? resolveEmptyProjectEnvironmentId(input.input);
  const dispatchUrl = resolvePrimaryEnvironmentHttpUrl("/api/orchestration/dispatch");

  await dispatchWebCreateThread({ ...input, webProjectId, dispatchUrl });
  const autoStarted = await dispatchWebStartTurn({ ...input, dispatchUrl });

  return {
    autoStarted,
    environmentId: webEnvironmentId,
    providerHint: input.launchModel.providerHint,
    threadId: input.threadId,
  };
}

type ResolvedThreadLaunchModel = NonNullable<ReturnType<typeof resolveThreadLaunchModel>>;
type ResolvedTaskThreadStartMetadata = ReturnType<typeof resolveTaskThreadStartMetadata>;

async function resolveWebProjectId(targetProjectRef: ProjectRef | null): Promise<string> {
  if (targetProjectRef) return targetProjectRef.projectId;

  const snapshotUrl = resolvePrimaryEnvironmentHttpUrl("/api/orchestration/snapshot");
  const snapshotRes = await fetch(snapshotUrl, buildPrimaryFetchInit({ credentials: "include" }));
  if (!snapshotRes.ok) {
    throw new Error(
      await readHttpErrorMessage(
        snapshotRes,
        "Could not connect to the Forge project to start a thread.",
      ),
    );
  }
  if (!isJsonResponse(snapshotRes)) {
    throw new Error(
      "Could not connect to the Forge project to start a thread. Import or link a project, then try again.",
    );
  }
  const snapshot = (await snapshotRes.json().catch(() => null)) as {
    projects?: ReadonlyArray<{ id: string }>;
  } | null;
  const serverProject = snapshot?.projects?.[0];
  if (!serverProject) {
    throw new Error("No project is available for starting a thread.");
  }
  return serverProject.id;
}

function isJsonResponse(response: Response): boolean {
  return (response.headers.get("content-type") ?? "").toLowerCase().includes("application/json");
}

function resolveEmptyProjectEnvironmentId(input: StartThreadForItemInput): EnvironmentId {
  const environmentId = input.primaryEnvironmentId;
  if (!environmentId) {
    throw new Error("Forge needs an active Command environment before it can open the thread.");
  }
  return environmentId;
}

async function dispatchWebCreateThread(input: {
  readonly input: StartThreadForItemInput;
  readonly webProjectId: string;
  readonly dispatchUrl: string;
  readonly launchModel: ResolvedThreadLaunchModel;
  readonly threadId: ThreadId;
  readonly threadCreatedAt: string;
  readonly startMetadata: ResolvedTaskThreadStartMetadata;
}): Promise<void> {
  const createRes = await fetch(
    input.dispatchUrl,
    buildPrimaryFetchInit({
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "thread.create",
        commandId: newCommandId(),
        threadId: input.threadId,
        projectId: input.webProjectId,
        title: input.startMetadata.threadTitle,
        modelSelection: input.launchModel.modelSelection,
        runtimeMode: resolveRuntimeMode(input.input),
        interactionMode: resolveInteractionMode(input.input),
        branch: resolveBaseBranch(input.input),
        worktreePath: null,
        ...resolveAnchorFields(input.startMetadata.cmmdTaskId),
        ...(input.input.payload.goal ? { goal: input.input.payload.goal } : {}),
        createdAt: input.threadCreatedAt,
      }),
    }),
  );
  if (!createRes.ok) {
    throw new Error(
      await readHttpErrorMessage(
        createRes,
        "Could not create the Forge thread. Import or link a project, then try again.",
      ),
    );
  }
}

async function dispatchWebStartTurn(input: {
  readonly input: StartThreadForItemInput;
  readonly dispatchUrl: string;
  readonly launchModel: ResolvedThreadLaunchModel;
  readonly canAutoStart: boolean;
  readonly threadId: ThreadId;
  readonly startMetadata: ResolvedTaskThreadStartMetadata;
  readonly targetProject: ThreadLaunchProject | null;
}): Promise<boolean> {
  const seedText = input.input.payload.seedPrompt.trim();
  if (!input.canAutoStart || !seedText) return false;

  const turnRes = await fetch(
    input.dispatchUrl,
    buildPrimaryFetchInit({
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "thread.turn.start",
        commandId: newCommandId(),
        threadId: input.threadId,
        message: buildSeedMessage(input.input.payload.seedPrompt),
        modelSelection: input.launchModel.modelSelection,
        titleSeed: input.startMetadata.threadTitle,
        runtimeMode: resolveRuntimeMode(input.input),
        interactionMode: resolveInteractionMode(input.input),
        ...resolveWorktreeBootstrap(input.input, input.targetProject),
        ...(input.startMetadata.cmmdTaskId ? { cmmdTaskId: input.startMetadata.cmmdTaskId } : {}),
        createdAt: new Date().toISOString(),
      }),
    }),
  );
  if (!turnRes.ok) {
    throw new Error(
      await readHttpErrorMessage(
        turnRes,
        "The thread was created, but Forge could not start the first message. Open the thread and retry.",
      ),
    );
  }
  return true;
}

async function startLocalThread(input: {
  readonly api: EnvironmentApi;
  readonly targetProjectRef: ProjectRef;
  readonly targetProject: ThreadLaunchProject | null;
  readonly launchConfiguration: ThreadLaunchConfiguration | undefined;
  readonly payload: TaskThreadStartPayload;
  readonly launchModel: ResolvedThreadLaunchModel;
  readonly canAutoStart: boolean;
  readonly threadId: ThreadId;
  readonly threadCreatedAt: string;
  readonly startMetadata: ResolvedTaskThreadStartMetadata;
}): Promise<ThreadLaunchResult> {
  if (!input.canAutoStart) {
    await dispatchLocalCreateThread(input);
    return {
      autoStarted: false,
      environmentId: input.targetProjectRef.environmentId,
      providerHint: input.launchModel.providerHint,
      threadId: input.threadId,
    };
  }

  await dispatchLocalStartTurn(input);
  return {
    autoStarted: true,
    environmentId: input.targetProjectRef.environmentId,
    providerHint: input.launchModel.providerHint,
    threadId: input.threadId,
  };
}

interface LocalThreadDispatchInput {
  readonly api: EnvironmentApi;
  readonly targetProjectRef: ProjectRef;
  readonly targetProject: ThreadLaunchProject | null;
  readonly launchConfiguration: ThreadLaunchConfiguration | undefined;
  readonly launchModel: ResolvedThreadLaunchModel;
  readonly threadId: ThreadId;
  readonly threadCreatedAt: string;
  readonly startMetadata: ResolvedTaskThreadStartMetadata;
  readonly payload: TaskThreadStartPayload;
}

async function dispatchLocalCreateThread(input: LocalThreadDispatchInput): Promise<void> {
  const createThread = buildLocalCreateThreadBootstrap(input);
  await input.api.orchestration.dispatchCommand({
    type: "thread.create",
    commandId: newCommandId(),
    threadId: input.threadId,
    ...createThread,
  });
}

async function dispatchLocalStartTurn(input: LocalThreadDispatchInput): Promise<void> {
  await input.api.orchestration.dispatchCommand(buildLocalStartTurnCommand(input));
}

function buildLocalStartTurnCommand(input: LocalThreadDispatchInput) {
  const createThread = buildLocalCreateThreadBootstrap(input);
  return {
    type: "thread.turn.start",
    commandId: newCommandId(),
    threadId: input.threadId,
    message: buildSeedMessage(input.payload.seedPrompt),
    modelSelection: input.launchModel.modelSelection,
    titleSeed: input.startMetadata.threadTitle,
    runtimeMode: createThread.runtimeMode,
    interactionMode: createThread.interactionMode,
    bootstrap: {
      createThread,
      ...resolveWorktreeBootstrapFromConfiguration(input),
    },
    ...(input.startMetadata.cmmdTaskId ? { cmmdTaskId: input.startMetadata.cmmdTaskId } : {}),
    createdAt: new Date().toISOString(),
  } as const;
}

function buildLocalCreateThreadBootstrap(input: LocalThreadDispatchInput) {
  return {
    projectId: input.targetProjectRef.projectId,
    title: input.startMetadata.threadTitle,
    modelSelection: input.launchModel.modelSelection,
    runtimeMode: input.launchConfiguration?.runtimeMode ?? DEFAULT_RUNTIME_MODE,
    interactionMode:
      input.launchConfiguration?.interactionMode ?? DEFAULT_PROVIDER_INTERACTION_MODE,
    branch: input.launchConfiguration?.baseBranch ?? null,
    worktreePath: null,
    ...resolveAnchorFields(input.startMetadata.cmmdTaskId),
    ...(input.payload.goal ? { goal: input.payload.goal } : {}),
    createdAt: input.threadCreatedAt,
  };
}

function resolveRuntimeMode(input: StartThreadForItemInput): RuntimeMode {
  return input.launchConfiguration?.runtimeMode ?? DEFAULT_RUNTIME_MODE;
}

function resolveInteractionMode(input: StartThreadForItemInput): ProviderInteractionMode {
  return input.launchConfiguration?.interactionMode ?? DEFAULT_PROVIDER_INTERACTION_MODE;
}

function resolveBaseBranch(input: StartThreadForItemInput): string | null {
  return input.launchConfiguration?.baseBranch ?? null;
}

function buildSeedMessage(seedPrompt: string) {
  return {
    messageId: newMessageId(),
    role: "user" as const,
    text: seedPrompt,
    attachments: [],
  };
}

function resolveAnchorFields(cmmdTaskId: string | null) {
  return cmmdTaskId ? { anchorKind: "task" as const, anchorCmmdId: cmmdTaskId } : {};
}
